import { useEffect, useRef, useState } from 'react';

/**
 * The DataTable's children-on-demand: reading a row's children when the row is opened.
 *
 * Two things ask for children, and they need different answers, which is why both live here:
 *
 * - **A click**, through `loadChildren`. Whether the children are already loaded, or stale, is a
 *   question about the consumer's data, so the consumer answers it: `onExpand` is called every time
 *   and returns nothing when there is nothing to fetch. That also means a load that failed can be
 *   retried by opening the row again.
 * - **A restore**, through the effect below. Which rows are open survives a reload, but their
 *   children do not — nothing asked for them — so each restored row has to be read once. This one
 *   cannot defer to the consumer: it re-runs as rows arrive, and a row whose fetch legitimately
 *   returns no children would be asked again on every pass. Hence `requestedRef`, which is about
 *   never asking twice for the same restored row rather than about the data being fresh.
 *
 * @param {object} args
 * @param {string} [args.childCountColumn] - Property holding a row's number of children. Without it
 *   the hook is inert: nothing says a row has children to read.
 * @param {Function} [args.onExpand] - The consumer's loader, called with the row item.
 * @param {object[]} [args.rows] - The table's current rows.
 * @param {string} args.idColumn
 * @param {string} args.parentIdColumn
 * @param {Array<string|number>} args.expandedItems - Ids of the rows currently open.
 * @param {string} [args.filterTerm] - The active filter term, if any.
 * @returns {{ loadingItems: Array<string|number>, loadChildren: Function }}
 */
export const useLazyChildren = ({
  childCountColumn,
  onExpand,
  rows,
  idColumn,
  parentIdColumn,
  expandedItems,
  filterTerm,
}) => {
  // Rows whose children are being fetched, so the expansion can show a loading state.
  const [loadingItems, setLoadingItems] = useState([]);

  // Restored rows that have been asked for. See the note above on why this is not a freshness check.
  const requestedRef = useRef(new Set());

  /**
   * Asks the consumer to load a row's children, and shows the row as loading until they arrive.
   * @param {string|number} itemId Value of the row's idColumn
   * @param {object} item The row item
   */
  const loadChildren = (itemId, item) => {
    if (!onExpand) return;

    requestedRef.current.add(itemId);

    const loading = onExpand(item);

    // A consumer that loads children lazily returns the fetch promise, and the row shows a loading
    // state until it settles. A synchronous handler returns nothing and nothing is tracked.
    if (typeof loading?.finally === 'function') {
      setLoadingItems(prev => [...prev, itemId]);
      loading.finally(() => setLoadingItems(prev => prev.filter(x => x !== itemId)));
    }
  };

  useEffect(() => {
    if (!childCountColumn || !onExpand || !rows?.length || expandedItems.length === 0) return;

    // Not for rows a filter opened. Filtering expands every row that survives it, which is a row
    // the user never opened — reading the children of each would be a request per match, and per
    // keystroke. What a filter needs from rows that were never loaded is the childFilter search,
    // which asks for the matching children themselves rather than for whole expansions.
    //
    // So while a filter is active the filter owns which rows are open, and a matching row shows the
    // children the search found under it and nothing else. Its toggle still works — being already
    // open, the first click closes it and the next opens it and loads, through loadChildren.
    if ((filterTerm || '').length > 1) return;

    const parents = new Set(rows.map(row => row[parentIdColumn]));

    expandedItems.forEach(itemId => {
      if (requestedRef.current.has(itemId) || parents.has(itemId)) return;

      const item = rows.find(row => row[idColumn] === itemId);
      // A row that no longer exists, or one whose children are not loaded on demand, has nothing to
      // ask for. The count is what says there is something to load.
      if (!item || !(item[childCountColumn] > 0)) return;

      loadChildren(itemId, item);
    });
    // Runs again as rows arrive: restoring a row deep in the tree can only ask once the rows above
    // it are there, and each batch that lands may hold another row that was left open. The filter
    // term is a dependency so that clearing a filter re-opens this path for the restored rows.
  }, [rows, expandedItems, filterTerm]);

  return { loadingItems, loadChildren };
};
